import DOMPurify from 'dompurify';
import { sanitizeUrl as braintreeSanitizeUrl } from '@braintree/sanitize-url';

/**
 * Enhanced XSS protection utilities for frontend sanitization
 * Enterprise-level security with obfuscation resistance and logging
 * Performance optimized with contextual pattern matching
 */

// Security event logging interface
interface SecurityEvent {
    type: 'xss_blocked' | 'url_blocked' | 'content_blocked';
    payload: string;
    reason: string;
    timestamp: Date;
    userAgent?: string;
    url?: string;
}

// Security logging configuration
let securityLoggingEnabled = true;
let securityEventCallback: ((event: SecurityEvent) => void) | null = null;

/**
 * Configure security logging
 */
export function configureSecurityLogging(enabled: boolean, callback?: (event: SecurityEvent) => void) {
    securityLoggingEnabled = enabled;
    securityEventCallback = callback || null;
}

/**
 * Log security events for monitoring and analysis
 */
function logSecurityEvent(event: Omit<SecurityEvent, 'timestamp' | 'userAgent' | 'url'>) {
    if (!securityLoggingEnabled) return;

    const fullEvent: SecurityEvent = {
        ...event,
        timestamp: new Date(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
    };

    // Call custom callback if provided
    if (securityEventCallback) {
        try {
            securityEventCallback(fullEvent);
        } catch (error) {
            console.error('Error in security event callback:', error);
        }
    }

    // Default logging to console (can be disabled in production)
    if (process.env.NODE_ENV === 'development') {
        console.warn('Security Event:', fullEvent);
    }
}

// Domain allowlist configuration
let domainAllowlist: Set<string> | null = null;

/**
 * Configure domain allowlist for URL validation
 */
export function configureDomainAllowlist(domains: string[] | null) {
    domainAllowlist = domains ? new Set(domains.map((d) => d.toLowerCase())) : null;
}

/**
 * Check if domain is in allowlist
 */
function isDomainAllowed(url: string): boolean {
    if (!domainAllowlist) return true; // No allowlist means all domains allowed

    try {
        const parsedUrl = new URL(url);
        const hostname = parsedUrl.hostname.toLowerCase();

        // Check exact match and subdomain matches
        return (
            domainAllowlist.has(hostname) ||
            Array.from(domainAllowlist).some((allowed) => hostname.endsWith(`.${allowed}`) || hostname === allowed)
        );
    } catch {
        return false;
    }
}

// Configuration for DOMPurify with strict security settings
// Hardened to prevent CSS injection bypasses
const STRICT_CONFIG = {
    ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'em',
        'u',
        'i',
        'b',
        'span',
        'div',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'a',
        'ul',
        'ol',
        'li',
    ],
    ALLOWED_ATTR: ['class', 'id', 'href', 'target', 'rel'],
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM_IMPORT: false,
    FORBID_SCRIPT: true,
    FORBID_TAGS: [
        'script',
        'object',
        'embed',
        'form',
        'input',
        'textarea',
        'select',
        'button',
        'noframe',
        'svg',
        'noscript',
        'style', // Explicitly forbid style tags
        'link', // Prevent external stylesheets
    ],
    FORBID_ATTR: [
        // Event handlers
        'onerror',
        'onload',
        'onclick',
        'onmouseover',
        'onfocus',
        'onblur',
        'onchange',
        'onsubmit',
        'onmouseout',
        'onmousedown',
        'onmouseup',
        'onkeydown',
        'onkeyup',
        'onkeypress',
        // Style attributes to prevent CSS injection
        'style',
        // Link attributes that could be dangerous (but allow href for links)
        'src',
        'action',
        'formaction',
        'data',
        // Other potentially dangerous attributes
        'background',
        'dynsrc',
        'lowsrc',
    ],
};

// Configuration for plain text (removes all HTML)
const PLAIN_TEXT_CONFIG = {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM_IMPORT: false,
};

/**
 * Contextual pattern groups for performance optimization
 * Patterns are grouped by context to avoid unnecessary checks
 */
const PATTERN_GROUPS = {
    html: [
        // Basic script patterns with obfuscation resistance
        /<\s*script[^>]*>/i,
        /<\s*\/\s*script\s*>/i,
        /<\s*iframe[^>]*srcdoc/i,
        /<\s*object[^>]*data/i,
        /<\s*embed[^>]*src/i,
        /<\s*link[^>]*href.*javascript/i,
        /<\s*meta[^>]*http-equiv.*refresh/i,
        // HTML comments that might hide malicious code
        /<!--[\s\S]*?-->/g,
    ],
    javascript: [
        // Consolidated JavaScript protocol detection (handles literal + obfuscated forms)
        /(?:j\s*a\s*v\s*a\s*s\s*c\s*r\s*i\s*p\s*t\s*:|&#x?6[aA];?&#x?61;?&#x?76;?&#x?61;?&#x?73;?&#x?63;?&#x?72;?&#x?69;?&#x?70;?&#x?74;?|\u006A\u0061\u0076\u0061\u0073\u0063\u0072\u0069\u0070\u0074)[\s\S]*?(?::|alert|eval|prompt)/i,
        // Event handlers with obfuscation
        /(?:on\w+\s*=|&#x?6[fF];?&#x?6[eE];?\w+\s*=)[\s\S]*?(?:javascript|eval|alert)/i,
        // DOM-based XSS patterns
        /(?:document|window|location)\s*\.\s*(?:write|writeln|cookie|location|domain|href|replace|assign|open|eval|setTimeout|setInterval)/i,
        // VBScript attacks
        /vbscript\s*:[\s\S]*?(?:msgbox|eval)/i,
    ],
    css: [
        // CSS-based attacks
        /expression\s*\(/i,
        /url\s*\(\s*javascript/i,
        /url\s*\(\s*data\s*:/i,
        /@import\s+.*javascript/i,
        // CSS/JS comments
        /\/\*[\s\S]*?\*\//g,
    ],
    dataUri: [
        // Data URI schemes with potential XSS
        /data\s*:\s*text\/html/i,
        /data\s*:\s*image\/svg\+xml/i,
        /data\s*:\s*application\/x-javascript/i,
    ],
    template: [
        // Template injection patterns - more specific to avoid false positives
        /\$\{[^}]*(?:eval|alert|prompt|confirm|setTimeout|setInterval|document\.|window\.|location\.)[^}]*\}/i,
        /\{\{[^}]*(?:constructor|__proto__|prototype|eval|alert|document\.|window\.)[^}]*\}\}/i,
    ],
    framework: [
        // Framework-specific patterns
        /ng-bind-html/i, // Angular
        /v-html/i, // Vue.js
        /dangerouslySetInnerHTML/i, // React
    ],
    evasion: [
        // Advanced evasion techniques
        /\\\w/g, // Backslash escapes
        // eslint-disable-next-line no-control-regex
        /[\x00-\x1f\x7f-\x9f]/g, // Control characters
    ],
};

/**
 * Get patterns based on context to improve performance
 */
function getContextualPatterns(context: 'html' | 'url' | 'general'): RegExp[] {
    switch (context) {
        case 'html':
            return [
                ...PATTERN_GROUPS.html,
                ...PATTERN_GROUPS.javascript,
                ...PATTERN_GROUPS.css,
                ...PATTERN_GROUPS.template,
                ...PATTERN_GROUPS.framework,
                ...PATTERN_GROUPS.evasion,
            ];
        case 'url':
            return [...PATTERN_GROUPS.javascript, ...PATTERN_GROUPS.dataUri, ...PATTERN_GROUPS.evasion];
        case 'general':
        default:
            return Object.values(PATTERN_GROUPS).flat();
    }
}

// Constants for safe limits
const MAX_DECODING_PASSES = 3;
const MAX_CONTENT_GROWTH = 5; // Max multiplier for content length during decoding
const MAX_BASE64_DECODE_LENGTH = 200; // Limit base64 decoding to prevent UI blocking
const MAX_BASE64_EXTRACTIONS = 5; // Limit total base64 extractions per string

function isEmptyContent(content: string | null | undefined): boolean {
    return !content || content.toLowerCase() === 'null';
}

/**
 * Enhanced malicious content detection with performance optimization
 */
function detectMaliciousContent(content: string, context: 'html' | 'url' | 'general' = 'general'): string | null {
    if (!content) return null;

    const originalLength = content.length;
    let testContent = content;

    // Limit content size for performance
    if (testContent.length > 10000) {
        testContent = testContent.substring(0, 10000);
    }

    // Limited decoding passes to prevent CPU exhaustion
    try {
        // HTML entity decoding (limited passes)
        let i = 0;
        while (i < MAX_DECODING_PASSES) {
            const decoded = testContent.replace(/&#x?[0-9a-f]+;/gi, (match) => {
                const num = match.startsWith('&#x')
                    ? parseInt(match.slice(3, -1), 16)
                    : parseInt(match.slice(2, -1), 10);
                return String.fromCharCode(num);
            });
            if (decoded === testContent) break;

            // Prevent excessive content growth
            if (decoded.length > originalLength * MAX_CONTENT_GROWTH) {
                return `Excessive content expansion during decoding in ${context}`;
            }

            testContent = decoded;
            i += 1;
        }

        // URL decoding (limited passes)
        let j = 0;
        while (j < MAX_DECODING_PASSES) {
            try {
                const decoded = decodeURIComponent(testContent);
                if (decoded === testContent) break;

                // Prevent excessive content growth
                if (decoded.length > originalLength * MAX_CONTENT_GROWTH) {
                    return `Excessive content expansion during URL decoding in ${context}`;
                }

                testContent = decoded;
            } catch {
                break; // Invalid encoding, stop trying
            }
            j += 1;
        }

        // Limited Base64 detection to prevent UI thread blocking
        const suspiciousBase64Contexts = [
            /(?:eval|exec|Function|setTimeout|setInterval)\s*\(\s*atob\s*\(\s*["']([A-Za-z0-9+/]{4,}={0,2})["']\s*\)/gi,
            /atob\s*\(\s*["']([A-Za-z0-9+/]{4,}={0,2})["']\s*\)/gi,
            /data\s*:\s*[^;]*;\s*base64\s*,\s*([A-Za-z0-9+/]{4,}={0,2})/gi,
            /src\s*=\s*["']data:[^;]*;base64,([A-Za-z0-9+/]{4,}={0,2})["']/gi,
        ];

        let base64ExtractionCount = 0;
        suspiciousBase64Contexts.forEach((pattern) => {
            const matches = Array.from(testContent.matchAll(pattern));
            matches.forEach((match) => {
                // Stop processing if we've hit the extraction limit
                if (base64ExtractionCount >= MAX_BASE64_EXTRACTIONS) {
                    return;
                }

                try {
                    const base64Data = match[1];
                    // Strict limits to prevent UI blocking
                    if (base64Data.length >= 8 && base64Data.length <= MAX_BASE64_DECODE_LENGTH) {
                        const decoded = atob(base64Data);
                        // Only add decoded content if it contains suspicious patterns
                        if (/(?:script|javascript|alert|eval|prompt|confirm)/i.test(decoded)) {
                            testContent += ` ${decoded}`;
                            base64ExtractionCount += 1;
                        }
                    }
                } catch {
                    // Invalid base64, ignore
                }
            });
        });
    } catch (error) {
        // If any decoding fails, the content might be malicious
        return `Decoding error in ${context}`;
    }

    // Test against contextual patterns for better performance
    const patterns = getContextualPatterns(context);
    const matchedPattern = patterns.find((pattern) => pattern.test(testContent));
    if (matchedPattern) {
        return `Malicious pattern detected in ${context}: ${matchedPattern.source}`;
    }

    return null;
}

/**
 * Strips all HTML tags and returns plain text using surgical threat removal
 * Similar to the backend approach but more secure and preserves legitimate content
 */
export function sanitizeToPlainText(content: string): string {
    if (isEmptyContent(content)) return '';

    try {
        // First, remove HTML tags while preserving text content
        let cleaned = DOMPurify.sanitize(content, PLAIN_TEXT_CONFIG) as string;

        // Then remove dangerous patterns surgically
        const dangerousPatterns = [
            /javascript\s*:/gi,
            /data\s*:\s*text\/html/gi,
            /data\s*:\s*image\/svg\+xml/gi,
            /vbscript\s*:/gi,
            /on\w+\s*=/gi,
            /expression\s*\(/gi,
            /url\s*\(\s*javascript/gi,
            /j\s*a\s*v\s*a\s*s\s*c\s*r\s*i\s*p\s*t\s*:/gi,
        ];

        dangerousPatterns.forEach((pattern) => {
            if (pattern.test(cleaned)) {
                logSecurityEvent({
                    type: 'content_blocked',
                    payload: cleaned.substring(0, 200),
                    reason: `Removed dangerous pattern: ${pattern.source}`,
                });
                cleaned = cleaned.replace(pattern, '');
            }
        });

        return cleaned.trim();
    } catch (error) {
        console.error('Error converting to plain text:', error);
        logSecurityEvent({
            type: 'content_blocked',
            payload: content.substring(0, 200),
            reason: 'DOMPurify sanitization failed',
        });
        // Ultimate fallback - basic text cleaning
        return content.replace(/<[^>]*>/g, '').trim();
    }
}

/**
 * Sanitizes HTML content with strict security settings
 * Better than the backend regex approach because it:
 * - Properly handles malformed HTML
 * - Preserves safe formatting
 * - Prevents XSS attacks comprehensively
 * - Uses surgical removal instead of discarding all content
 */
export function sanitizeHtml(content: string | null | undefined): string {
    if (isEmptyContent(content)) return '';

    const stringContent = String(content);

    try {
        // Use DOMPurify first to remove dangerous elements while preserving structure
        const domPurified = DOMPurify.sanitize(stringContent, STRICT_CONFIG) as string;

        // Then remove any remaining dangerous patterns surgically
        let cleaned = domPurified;
        const dangerousPatterns = [
            /javascript\s*:/gi,
            /data\s*:\s*text\/html/gi,
            /data\s*:\s*image\/svg\+xml/gi,
            /vbscript\s*:/gi,
            /on\w+\s*=/gi,
            /expression\s*\(/gi,
            /url\s*\(\s*javascript/gi,
            /j\s*a\s*v\s*a\s*s\s*c\s*r\s*i\s*p\s*t\s*:/gi,
        ];

        dangerousPatterns.forEach((pattern) => {
            if (pattern.test(cleaned)) {
                logSecurityEvent({
                    type: 'xss_blocked',
                    payload: cleaned.substring(0, 200),
                    reason: `Removed dangerous pattern: ${pattern.source}`,
                });
                cleaned = cleaned.replace(pattern, '');
            }
        });

        return cleaned;
    } catch (error) {
        console.error('Error sanitizing HTML:', error);
        logSecurityEvent({
            type: 'xss_blocked',
            payload: stringContent.substring(0, 200),
            reason: 'DOMPurify sanitization failed',
        });
        // Fallback to plain text if sanitization fails
        return sanitizeToPlainText(stringContent);
    }
}

/**
 * Enhanced sanitization for descriptions that may contain markdown
 * Handles both HTML and markdown content safely
 * Removes only malicious parts while preserving legitimate content
 */
export function sanitizeDescription(description: string | null | undefined): string {
    if (isEmptyContent(description)) return '';

    const stringContent = String(description);

    // First pass: sanitize any HTML using DOMPurify
    let cleaned = sanitizeHtml(description);

    // Remove dangerous patterns while preserving safe content
    const dangerousPatterns = [
        /javascript\s*:/gi,
        /data\s*:\s*text\/html/gi,
        /data\s*:\s*image\/svg\+xml/gi,
        /vbscript\s*:/gi,
        /on\w+\s*=/gi,
        /expression\s*\(/gi,
        /url\s*\(\s*javascript/gi,
        /j\s*a\s*v\s*a\s*s\s*c\s*r\s*i\s*p\s*t\s*:/gi,
    ];

    // Remove only the dangerous patterns, keep everything else
    dangerousPatterns.forEach((pattern) => {
        if (pattern.test(cleaned)) {
            logSecurityEvent({
                type: 'content_blocked',
                payload: cleaned.substring(0, 200),
                reason: `Removed dangerous pattern: ${pattern.source}`,
            });
            cleaned = cleaned.replace(pattern, '');
        }
    });

    // Decode HTML entities safely
    try {
        cleaned = cleaned.replace(/&#x?[0-9a-f]+;/gi, (match) => {
            try {
                const num = match.startsWith('&#x')
                    ? parseInt(match.slice(3, -1), 16)
                    : parseInt(match.slice(2, -1), 10);
                const decoded = String.fromCharCode(num);

                // Check if the decoded character creates dangerous patterns
                const testString = cleaned.replace(match, decoded);
                const hasDangerousPattern = dangerousPatterns.some((pattern) => pattern.test(testString));

                if (hasDangerousPattern) {
                    logSecurityEvent({
                        type: 'content_blocked',
                        payload: match,
                        reason: 'HTML entity decoded to dangerous pattern',
                    });
                    return ''; // Remove the dangerous entity
                }

                return decoded; // Safe to decode
            } catch {
                return ''; // Invalid entity, remove it
            }
        });
    } catch (error) {
        // If decoding fails completely, log but don't return empty
        logSecurityEvent({
            type: 'content_blocked',
            payload: stringContent.substring(0, 200),
            reason: 'HTML entity decoding failed partially',
        });
    }

    return cleaned.trim();
}

/**
 * Validates if content is safe for display
 * Returns true if content passes all security checks
 */
export function isContentSafe(content: string): boolean {
    if (!content) return true;

    const threat = detectMaliciousContent(content, 'general');
    if (threat) {
        logSecurityEvent({
            type: 'content_blocked',
            payload: content.substring(0, 200),
            reason: threat,
        });
        return false;
    }

    return true;
}

/**
 * Enhanced validation layer for DataHub-specific threats
 * Focuses on JSONP/callback attacks and advanced evasion techniques
 */
function applyEnhancedValidation(url: string): string {
    if (!url) return '';

    const trimmedUrl = url.trim();

    // Check domain allowlist first
    if (!isDomainAllowed(trimmedUrl)) {
        logSecurityEvent({
            type: 'url_blocked',
            payload: trimmedUrl,
            reason: 'Domain not in allowlist',
        });
        return '';
    }

    // Check for malicious content
    const threat = detectMaliciousContent(trimmedUrl, 'url');
    if (threat) {
        logSecurityEvent({
            type: 'url_blocked',
            payload: trimmedUrl,
            reason: threat,
        });
        return '';
    }

    // Safer callback validation using allowlist approach instead of regex hunting
    // This prevents sophisticated obfuscation attacks like callback=(0,alert)(1) or callback=window
    const queryParams = trimmedUrl.split('?')[1] || '';
    const callbackParamPattern = /[?&]\w*(?:callback|jsonp|cb|fn|mycallback|jsonpcallback)\w*=([^&]*)/gi;
    const callbackMatches = Array.from(queryParams.matchAll(callbackParamPattern));

    const unsafeCallback = callbackMatches.find((match) => {
        const callbackValue = decodeURIComponent(match[1] || '');

        // Enforce strict alphanumeric/underscore policy for callback names
        // This is much safer than trying to detect all possible attack vectors
        const safeCallbackPattern = /^[a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)*$/;

        if (!safeCallbackPattern.test(callbackValue)) {
            logSecurityEvent({
                type: 'url_blocked',
                payload: trimmedUrl,
                reason: `Unsafe callback parameter: ${callbackValue}`,
            });
            return true;
        }

        // Additional length check to prevent excessive callback names
        if (callbackValue.length > 100) {
            logSecurityEvent({
                type: 'url_blocked',
                payload: trimmedUrl,
                reason: 'Callback parameter too long',
            });
            return true;
        }

        return false;
    });

    if (unsafeCallback) {
        return '';
    }

    // Additional DataHub-specific pattern detection - refined to reduce false positives
    const enhancedPatterns = [
        /\b(?:top|parent|self|frames)\.\s*(?:location|open|eval)/i, // Frame busting attempts
        /\/\*[\s\S]*?(?:script|javascript|eval|alert)[\s\S]*?\*\//i, // Comment injection with suspicious content
        /\\x[0-9a-f]{2}/i, // Hex escape sequences (often used in attacks)
    ];

    if (enhancedPatterns.some((pattern) => pattern.test(trimmedUrl))) {
        logSecurityEvent({
            type: 'url_blocked',
            payload: trimmedUrl,
            reason: 'Enhanced pattern validation failed',
        });
        return '';
    }

    return trimmedUrl;
}

/**
 * Validates if a URL uses safe HTTP/HTTPS protocols
 */
export function isValidHttpUrl(url: string): boolean {
    try {
        if (!url || typeof url !== 'string') {
            return false;
        }
        const trimmedUrl = url.trim();
        if (!trimmedUrl) {
            return false;
        }
        const parsedUrl = new URL(trimmedUrl);
        return ['http:', 'https:'].includes(parsedUrl.protocol);
    } catch {
        return false;
    }
}

/**
 * Hybrid URL sanitization combining battle-tested library with enhanced protections
 * Uses @braintree/sanitize-url for baseline security + custom logic for advanced threats
 */
export function sanitizeUrl(url: string): string {
    if (!url || typeof url !== 'string') return '';

    // Step 1: Use battle-tested Braintree library for baseline protection
    // Handles: basic XSS, encoding, Unicode normalization, protocol filtering
    const braintreeResult = braintreeSanitizeUrl(url);

    // If Braintree blocked it, respect that decision
    if (braintreeResult === 'about:blank' || braintreeResult === '') {
        logSecurityEvent({
            type: 'url_blocked',
            payload: url.substring(0, 200),
            reason: 'Blocked by Braintree sanitization',
        });
        return '';
    }

    // Step 2: Apply DataHub-specific enhanced protections
    return applyEnhancedValidation(braintreeResult);
}

/**
 * Replaces the basic backend regex approach with comprehensive frontend sanitization
 * This is safer than the Java backend code because it:
 * 1. Handles edge cases and malformed HTML properly
 * 2. Uses a battle-tested sanitization library
 * 3. Can preserve safe formatting when needed
 * 4. Provides multiple sanitization levels
 */
export function replaceLegacySanitization(content: string, allowFormatting = true): string {
    if (allowFormatting) {
        return sanitizeDescription(content);
    }
    return sanitizeToPlainText(content);
}

/**
 * Form validation utilities for consistent validation across components
 */

/**
 * URL validation rule factory for Ant Design Form validation
 * @param t - Translation function
 * @param customMessage - Custom message for HTTP/HTTPS validation
 * @returns Validation rule for URL fields
 */
export function createUrlValidationRule(t: (key: string) => string, customMessage?: string) {
    return {
        validator: (_: any, value: string) => {
            if (!value) return Promise.resolve();

            // Sanitize the URL first
            const sanitizedUrl = sanitizeUrl(value);

            // Validate HTTP/HTTPS protocols
            if (!isValidHttpUrl(sanitizedUrl)) {
                const message = customMessage || t('Please enter a valid HTTP or HTTPS URL');
                return Promise.reject(new Error(message));
            }

            // Check content safety
            if (!isContentSafe(value)) {
                return Promise.reject(new Error(t('URL contains potentially unsafe content')));
            }

            return Promise.resolve();
        },
    };
}

/**
 * Text validation rule factory for Ant Design Form validation
 * @param t - Translation function
 * @param fieldName - Name of the field for error messages
 * @param maxLength - Maximum allowed length (default: 200)
 * @returns Validation rule for text fields
 */
export function createTextValidationRule(t: (key: string) => string, fieldName: string, maxLength = 200) {
    return {
        validator: (_: any, value: string) => {
            if (!value) return Promise.resolve();

            // Check content safety for the text
            if (!isContentSafe(value)) {
                return Promise.reject(new Error(t(`${fieldName} contains potentially unsafe content`)));
            }

            // Check if text is too long
            if (value.length > maxLength) {
                return Promise.reject(new Error(t(`${fieldName} must be ${maxLength} characters or less`)));
            }

            return Promise.resolve();
        },
    };
}

/**
 * Real-time form validation handler factory
 * @param form - Ant Design form instance
 * @param setIsFormValid - Function to update form validity state
 * @param requiredFields - Array of required field names
 * @returns onFieldsChange handler function
 */
export function createFormValidationHandler(
    form: any,
    setIsFormValid: (valid: boolean) => void,
    requiredFields: string[] = [],
) {
    return () => {
        const hasErrors = form.getFieldsError().some((field: any) => field.errors.length > 0);

        // Check if all required fields have values
        const hasAllRequiredFields = requiredFields.every((fieldName) => !!form.getFieldValue(fieldName));

        // Check for content safety in real-time for all fields
        const fieldValues = form.getFieldsValue();
        const hasUnsafeContent = Object.values(fieldValues).some(
            (value: any) => value && typeof value === 'string' && !isContentSafe(value),
        );

        setIsFormValid(hasAllRequiredFields && !hasErrors && !hasUnsafeContent);
    };
}

/**
 * URL input blur handler factory
 * @param form - Ant Design form instance
 * @param fieldName - Name of the URL field
 * @returns onBlur handler function
 */
export function createUrlBlurHandler(form: any, fieldName: string) {
    return (e: React.FocusEvent<HTMLInputElement>) => {
        const sanitized = sanitizeUrl(e.target.value);
        if (sanitized !== e.target.value) {
            form.setFieldValue(fieldName, sanitized);
        }
    };
}

/**
 * Text input blur handler factory
 * @param form - Ant Design form instance
 * @param fieldName - Name of the text field
 * @returns onBlur handler function
 */
export function createTextBlurHandler(form: any, fieldName: string) {
    return (e: React.FocusEvent<HTMLInputElement>) => {
        const sanitized = sanitizeToPlainText(e.target.value);
        if (sanitized !== e.target.value) {
            form.setFieldValue(fieldName, sanitized);
        }
    };
}
